/**
 * The head on a plugin's own face: what this face is, whose it is, and -- when
 * the shell can honestly read it -- what the Agent last did to it.
 *
 * 图二's card head is three things on one line: 「路线比较 · 界面插件 · Atlas
 * 已按本次任务调整」. Two of the three are always true and come from the
 * declaration and the directory; the third is a claim about the past, so it is
 * drawn only when the server answers with one (`surface-head`) and is silent
 * otherwise. A head that guessed would be the console quoting itself.
 *
 * The task is shown as the person's own words, marked `data-quoted` -- the
 * vocabulary rule's one exemption, and the reason it exists: an Owner who typed
 * a sentence at their Agent must see that sentence back.
 */

import { useEffect, useState } from "react";
import { api, type SurfaceChange } from "../lib/api";
import { surfaceAgentName, UNNAMED_AGENT } from "./surfaceAgent";
import { traceHref } from "./surfaceTrace";
import { useWorkTool } from "../shell/workTools";

export function SurfaceCardHead({
  panelKey,
  title,
  compact = false,
  /** Bumped by the host when something this face did may have finished. */
  version = 0,
  inBar = false,
}: {
  panelKey: string;
  /** The block's own declared title, preferred over the panel's. */
  title?: string;
  compact?: boolean;
  version?: number;
  /** The Agent's bar names the page and keeps the last change under 更多; this head draws nothing of its own. */
  inBar?: boolean;
}) {
  const [agent, setAgent] = useState(UNNAMED_AGENT);
  const [panelTitle, setPanelTitle] = useState("");
  const [change, setChange] = useState<SurfaceChange | null>(null);

  useEffect(() => {
    let live = true;
    void surfaceAgentName().then(name => { if (live) setAgent(name); });
    return () => { live = false; };
  }, []);

  // A head that cannot be read is a head with two facts instead of three, not
  // an error page: the face itself is fine, and the frame below it is the point.
  useEffect(() => {
    let live = true;
    api.surfaceHead(panelKey)
      .then(facts => { if (live) { setPanelTitle(facts.title); setChange(facts.change); } })
      .catch(() => { if (live) setChange(null); });
    return () => { live = false; };
  }, [panelKey, version]);

  const name = title || panelTitle;
  useWorkTool("change", change ? { agent, task: change.task, ...(change.turn_id ? { trace: traceHref({ turn: change.turn_id, about: name }) } : {}) } : undefined, inBar);
  if (!name || inBar) return null;
  return (
    <header className="surface-card-head">
      <h3 className="surface-card-title">{name}</h3>
      <span className="surface-card-agent">{agent} 的界面</span>
      {change && !compact ? (
        <details className="surface-card-history">
          <summary>界面记录</summary>
          <div>
            <p className="surface-card-change">{agent} 按 <q data-quoted>{change.task}</q> 调过</p>
            {change.turn_id ? <a className="btn btn-quiet surface-card-trace" href={traceHref({ turn: change.turn_id, about: name })}>查看执行过程</a> : null}
          </div>
        </details>
      ) : null}
      {change?.turn_id && compact ? (
        <a className="btn btn-quiet surface-card-trace" href={traceHref({ turn: change.turn_id, about: name })}>
          查看执行过程
        </a>
      ) : null}
    </header>
  );
}
